import React, { useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { CloudUpload, InsertDriveFile, Close } from "@mui/icons-material";
import { ACCEPT_FILES, validateFile } from "../utils/fileValidation";

const FileUploadField = ({
  label,
  required,
  value,
  onChange,
  error,
  disabled = false,
  readOnly = false,
}) => {
  const inputRef = useRef(null);
  const isInteractive = !disabled && !readOnly;

  const [localError, setLocalError] = useState(null);
  const displayError = localError || error;

  const isFile = value instanceof File;
  const isUrl = typeof value === "string" && value.trim() !== "";

  const fileName = isFile
    ? value.name
    : isUrl
      ? decodeURIComponent(
          value.split("/").pop()?.split("?")[0] || "Uploaded document",
        )
      : "";

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const validationError = await validateFile(file);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(null);
    onChange(file);
  };

  const handleView = () => {
    if (isUrl) {
      window.open(value, "_blank", "noopener,noreferrer");
    }
  };

  const handleUploadClick = () => {
    if (!isInteractive) return;
    inputRef.current?.click();
  };

  const handleRemove = () => {
    setLocalError(null);
    onChange(null);
  };

  return (
    <Box>
      <Typography
        sx={{ fontSize: 12.5, fontWeight: 650, color: "#3A4441", mb: 0.75 }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: "#B3261E" }}>
            {" "}
            *
          </Box>
        )}
      </Typography>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_FILES}
        hidden
        onChange={handleFileSelect}
      />

      {value ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: "1px solid #E6ECEA",
            borderRadius: 2,
            p: 0.75,
            backgroundColor: "#FAFBFA",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <InsertDriveFile sx={{ color: "#0E7A6E", fontSize: 20 }} />

          <Typography
            onClick={handleView}
            sx={{
              fontSize: 13,
              flex: 1,
              color: "#12181A",
              cursor: isUrl ? "pointer" : "default",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": isUrl ? { textDecoration: "underline" } : {},
            }}
          >
            {fileName}
          </Typography>

          {isInteractive && (
            <IconButton size="small" onClick={handleRemove}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box
          onClick={handleUploadClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: `1px dashed ${error ? "#B3261E" : "#E6ECEA"}`,
            borderRadius: 2,
            p: 1.25,
            cursor: isInteractive ? "pointer" : "default",
            color: "#66766F",
            "&:hover": isInteractive
              ? {
                  borderColor: "#0E7A6E",
                  backgroundColor: "#E6F5F2",
                  color: "#0B5F56",
                }
              : {},
          }}
        >
          <CloudUpload sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: 13 }}>
            {isInteractive ? "Click to upload" : "No document uploaded"}
          </Typography>
        </Box>
      )}

      {error && (
        <Typography sx={{ fontSize: 11.5, color: "#B3261E", mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FileUploadField;
